// ═══════════════════════════════════════════════════════════════════════════
// ⏳ GECICI SAYFA — KAMERA AYAR PANELI (2026-08-04)
// ═══════════════════════════════════════════════════════════════════════════
// AMAC: Musteri "netlik ve isik" icin en iyi ayari KENDI GOZUYLE secsin.
// Secim DB'ye yazilir ve o andan itibaren ESAS AYAR olur (kabin 60 sn icinde alir).
//
// 🔻 BU SAYFA SONRADAN KALDIRILACAK.
//    Sokme recetesi: SocialBooth-Backend/KALDIR-kamera-ayar-paneli.md
//    Kaldirirken musterinin son sectigi degerler koda varsayilan olarak yazilir.
//
// NOT: Bu dosya BILINCLI olarak ASCII-Turkce kullanir (mevcut admin sayfalariyla
// ayni kural) — CRA build'inde karakter bozulmasi yasanmasin diye.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
} from 'reactstrap'
import BreadCrumb from 'Components/Common/BreadCrumb'
import { toast } from 'react-toastify'
import { fetchCameraSettings, updateCameraSettings } from 'helpers/backend_helper'
import { getPermissions } from 'lib/permissionUtils'

type FlowKey = 'selfie' | 'roll'

interface FlowSettings {
  brightness: number
  correction: number
  sharpness: number
  shutter: string
  personQuality: string
  bgDepth: string
}

const DEFAULT_FLOW: FlowSettings = {
  brightness: 132,
  correction: 2.2,
  sharpness: 1.0,
  shutter: '',
  personQuality: 'high',
  bgDepth: 'off',
}

interface Option {
  label: string
  value: number | string
}

const BRIGHTNESS_OPTIONS: Option[] = [
  { label: 'Cok koyu', value: 118 },
  { label: 'Koyu', value: 125 },
  { label: 'Normal (onerilen)', value: 132 },
  { label: 'Aydinlik', value: 140 },
  { label: 'Cok aydinlik', value: 148 },
]

// NOT: "Normal" degeri 2.2, kiosk'taki TONE_MAX_GAIN varsayilaniyla AYNI olmak
// ZORUNDA — aksi halde kayitli varsayilan bu listede bulunamaz ve acilir menu
// yanlis secenegi gosterir (kullanici degistirmedigi halde degismis sanir).
const CORRECTION_OPTIONS: Option[] = [
  { label: 'Kapali - kameradan geldigi gibi bas', value: 1.0 },
  { label: 'Hafif', value: 1.5 },
  { label: 'Normal (onerilen)', value: 2.2 },
  { label: 'Guclu', value: 2.6 },
  { label: 'En guclu', value: 3.0 },
]

const SHARPNESS_OPTIONS: Option[] = [
  { label: 'Yumusak', value: 0.5 },
  { label: 'Normal (onerilen)', value: 1.0 },
  { label: 'Keskin', value: 1.4 },
  { label: 'Cok keskin', value: 1.8 },
]

const SHUTTER_OPTIONS: Option[] = [
  { label: 'Kabin ayarini kullan (onerilen)', value: '' },
  { label: '1/30 - en aydinlik, hareket bulanikligi riski', value: '1/30' },
  { label: '1/50', value: '1/50' },
  { label: '1/60 - dengeli', value: '1/60' },
  { label: '1/80', value: '1/80' },
  { label: '1/100', value: '1/100' },
  { label: '1/125 - en net, karanlikta gren artar', value: '1/125' },
]

// Degerleri backend'deki ALLOWED_PERSON_QUALITY ile BIREBIR ayni olmak zorunda
// (test-camera-settings.js drift testi bunu kolluyor).
const PERSON_QUALITY_OPTIONS: Option[] = [
  { label: 'Yuksek (onerilen)', value: 'high' },
  { label: 'Dusuk - kabin cekimde takilirsa', value: 'low' },
]

// Degerleri backend'deki ALLOWED_BG_DEPTH ile BIREBIR ayni olmak zorunda.
const BG_DEPTH_OPTIONS: Option[] = [
  { label: 'Kapali (varsayilan) - arka plan tam keskin', value: 'off' },
  { label: 'Hafif', value: 'light' },
  { label: 'Belirgin', value: 'strong' },
]

interface FieldDef {
  key: keyof FlowSettings
  label: string
  help: string
  options: Option[]
  /** Bu alan yalniz listelenen akislarda gosterilir. Bos = her akista.
   *  Hicbir sey yapmayan bir dugme gostermek guven kaybettirir; personQuality
   *  photoroll'da ETKISIZ oldugu icin orada hic cikmaz. */
  onlyFlows?: FlowKey[]
}

const FIELDS: FieldDef[] = [
  {
    key: 'brightness',
    label: 'Isik / Parlaklik (hedef)',
    help: 'Baskinin genel aydinligi. Yuz koyu cikiyorsa artirin. Daha aydinlik sectiginiz halde fark gormuyorsaniz, asagidaki "duzeltme gucu"nu de yukseltin.',
    options: BRIGHTNESS_OPTIONS,
  },
  {
    key: 'correction',
    label: 'Otomatik duzeltme gucu (siniri)',
    help: 'Yukaridaki hedefe ulasmak icin en fazla ne kadar aydinlatilabilecegini belirler. Cok karanlik karelerde hedefe ancak yuksek deger ile ulasilir. "Kapali" secilirse hicbir duzeltme yapilmaz.',
    options: CORRECTION_OPTIONS,
  },
  {
    key: 'sharpness',
    label: 'Netlik (keskinlestirme)',
    help: 'Cok yuksek degerde kenarlarda beyaz hale olusabilir.',
    options: SHARPNESS_OPTIONS,
  },
  {
    key: 'shutter',
    label: 'Deklansor hizi (fotograf makinesi)',
    help: 'Hareket bulanikligini bu belirler. Hizli deklansor = daha net kisi, ama karanlik ortamda gren artar.',
    options: SHUTTER_OPTIONS,
  },
  {
    key: 'personQuality',
    label: 'Kisi kesme kalitesi',
    help: 'Kisinin arka plandan kesildigi kenarin (sac, omuz) keskinligi. "Yuksek" secildiginde kenardaki yari saydam bulanik serit inceltilir, kisi arka plana daha temiz oturur; cekim birkac yuz milisaniye uzayabilir. Kabin cekimde takiliyorsa "Dusuk" secin. NOT: bu ayar kisinin YUZ/CILT detayini DEGISTIRMEZ — onu fotograf makinesinin cekim yapip yapamadigi belirler.',
    options: PERSON_QUALITY_OPTIONS,
    onlyFlows: ['selfie'],
  },
  {
    key: 'bgDepth',
    label: 'Arka plan alan derinligi',
    help: 'Secilen arka plan gorseline hafif bir bulaniklik ve fotografin tamamina ortak bir gren verir. AMAC: gercek bir portrede oldugu gibi kisi net, arka plan yumusak olsun. Jilet gibi keskin bir arka planin yaninda, makineden gelen kisi oldugundan daha bulanik gorunur; bu ayar o kiyasi ortadan kaldirir ve kompozit "kesilip yapistirilmis" degil FOTOGRAF gibi okunur. Kisinin pikselleri HIC DEGISMEZ. UYARI: arka plan gorselindeki yazilar tam okunur kalmali ise "Kapali" birakin. Once "Hafif" ile deneyin, ciktiyi karsilastirin.',
    options: BG_DEPTH_OPTIONS,
    onlyFlows: ['selfie'],
  },
]

const FLOWS: { key: FlowKey; title: string; icon: string; desc: string }[] = [
  {
    key: 'selfie',
    title: 'Buyulu Selfie',
    icon: 'mdi mdi-account-star',
    desc: 'Arka planin degistirildigi tekli fotograf akisi.',
  },
  {
    key: 'roll',
    title: 'Photoroll',
    icon: 'mdi mdi-image-multiple',
    desc: 'Ardisik 3 pozluk serit akisi.',
  },
]

const CameraSettings = () => {
  // NOT: Gezinme metinleri (menu / sekme basligi / breadcrumb) admin panelinin
  // geri kalaniyla ayni sekilde INGILIZCE'dir (bkz. Timer Settings); yardim
  // metinleri musteri okusun diye Turkce kalir.
  document.title = 'Camera Settings | SocialBooth - Admin Dashboard'

  // Yetkisi olmayan operatore CALISMAYAN dugme gosterilmez: backend zaten 403
  // dondurur, kullanici sebebini anlayamaz (4 Agu dersi: hicbir sey yapmayan
  // dugme = guven kaybi).
  const { canEdit } = getPermissions('Camera Settings')

  const [loading, setLoading] = useState<boolean>(true)
  const [savingFlow, setSavingFlow] = useState<FlowKey | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string>('')
  const [form, setForm] = useState<Record<FlowKey, FlowSettings>>({
    selfie: { ...DEFAULT_FLOW },
    roll: { ...DEFAULT_FLOW },
  })

  const load = async () => {
    try {
      setLoading(true)
      const res: any = await fetchCameraSettings()
      if (res?.success && res?.data) {
        const d = res.data
        setForm({
          selfie: { ...DEFAULT_FLOW, ...(d.selfie || {}) },
          roll: { ...DEFAULT_FLOW, ...(d.roll || {}) },
        })
        if (d.updatedAt) setUpdatedAt(new Date(d.updatedAt).toLocaleString('tr-TR'))
      }
    } catch (e) {
      toast.error('Kamera ayarlari yuklenemedi.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const setField = (flow: FlowKey, key: keyof FlowSettings, raw: string) => {
    setForm((prev) => ({
      ...prev,
      [flow]: {
        ...prev[flow],
        [key]: key === 'shutter' || key === 'personQuality' ? raw : Number(raw),
      },
    }))
  }

  const save = async (flow: FlowKey) => {
    try {
      setSavingFlow(flow)
      // Yalniz DEGISTIRILEN akis gonderilir — diger akisin ayari kazara ezilmesin.
      const res: any = await updateCameraSettings({ [flow]: form[flow] })
      if (res?.success) {
        toast.success(
          `${flow === 'selfie' ? 'Buyulu Selfie' : 'Photoroll'} ayari kaydedildi. Kabin en gec 1 dakika icinde uygular.`
        )
        if (res.data?.updatedAt)
          setUpdatedAt(new Date(res.data.updatedAt).toLocaleString('tr-TR'))
      } else {
        toast.error(res?.msg || 'Kaydedilemedi.')
      }
    } catch (e) {
      toast.error('Kamera ayarlari kaydedilemedi.')
    } finally {
      setSavingFlow(null)
    }
  }

  const resetFlow = (flow: FlowKey) => {
    setForm((prev) => ({ ...prev, [flow]: { ...DEFAULT_FLOW } }))
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Camera Settings" pageTitle="Settings" />

          <Alert color="info" className="d-flex align-items-start">
            <i className="mdi mdi-information-outline fs-20 me-2"></i>
            <div>
              <strong>Nasil kullanilir?</strong> Bir ayari degistirip kaydedin, kabinde
              bir cekim yapip baskiyi inceleyin. Begenmediyseniz baska bir secenegi
              deneyin. Kabin yeni ayari <strong>en gec 1 dakika</strong> icinde alir;
              yeniden baslatmak GEREKMEZ.
              <br />
              Iki akis <strong>birbirinden bagimsizdir</strong> — her birinin kendi
              kaydet butonu vardir.
              {updatedAt && (
                <>
                  <br />
                  <small className="text-muted">Son degisiklik: {updatedAt}</small>
                </>
              )}
            </div>
          </Alert>

          {!canEdit && (
            <Alert color="warning" className="d-flex align-items-start">
              <i className="mdi mdi-lock-outline fs-20 me-2"></i>
              <div>
                Bu sayfayi yalnizca <strong>goruntuleme</strong> yetkiniz var; ayarlari
                degistiremezsiniz. Degistirmek icin yoneticinizden{' '}
                <strong>Camera Settings &rarr; Edit</strong> yetkisi isteyin.
              </div>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Row>
              {FLOWS.map((flow) => (
                <Col lg={6} key={flow.key}>
                  <Card>
                    <CardHeader className="d-flex align-items-center">
                      <i className={`${flow.icon} fs-18 me-2 text-primary`}></i>
                      <div>
                        <h5 className="card-title mb-0">{flow.title}</h5>
                        <small className="text-muted">{flow.desc}</small>
                      </div>
                    </CardHeader>
                    <CardBody>
                      {FIELDS.filter(
                        (f) => !f.onlyFlows || f.onlyFlows.includes(flow.key)
                      ).map((field) => {
                        const current = String(form[flow.key][field.key])
                        // DB'deki deger bu listede yoksa <select> SESSIZCE ilk
                        // secenegi gosterir → kullanici ayari degismis sanir ve
                        // farkinda olmadan uzerine yazar. Bilinmeyen degeri
                        // acikca goster.
                        const known = field.options.some((o) => String(o.value) === current)
                        const options = known
                          ? field.options
                          : [{ label: `Ozel deger: ${current}`, value: current }, ...field.options]
                        return (
                          <div className="mb-3" key={field.key}>
                            <Label className="form-label fw-semibold">{field.label}</Label>
                            <Input
                              type="select"
                              value={current}
                              disabled={!canEdit}
                              onChange={(e) => setField(flow.key, field.key, e.target.value)}
                            >
                              {options.map((o) => (
                                <option key={String(o.value)} value={String(o.value)}>
                                  {o.label}
                                </option>
                              ))}
                            </Input>
                            <small className="text-muted">{field.help}</small>
                          </div>
                        )
                      })}

                      <div className="d-flex gap-2 mt-4">
                        <Button
                          color="primary"
                          onClick={() => save(flow.key)}
                          disabled={savingFlow !== null || !canEdit}
                        >
                          {savingFlow === flow.key ? (
                            <>
                              <Spinner size="sm" className="me-1" /> Kaydediliyor
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-content-save me-1"></i>
                              {flow.title} ayarini kaydet
                            </>
                          )}
                        </Button>
                        <Button
                          color="light"
                          onClick={() => resetFlow(flow.key)}
                          disabled={savingFlow !== null || !canEdit}
                        >
                          Onerilene don
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default CameraSettings
